import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { CommonDatePickerHeader } from './common-date-picker-header';
import './styles/index.scss';
export type CommonDatePickerProps = {
  onSelection?: (startDate: Date | null, endDate: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
  label?: string;
  placeholder?: string;
  showRange?: boolean;
  classNames?: any;
  selectsRange?: boolean;
  iconRight?: React.ReactElement;
  iconLeft?: React.ReactElement;
  showActiveBackground?: boolean;
};

const TEXT_CONSTANTS = {
  DAY: 'day',
  MONTH: 'month',
  YEAR: 'year'
};

/** returns the years range that is shown in the year picker*/
const getYearRange = (date: Date) => {
  const dateYear = date.getFullYear();
  let rangeStart = dateYear;
  let count = 0;
  while (!Number.isInteger((dateYear - count) / 12)) {
    count++;
  }
  if (Number.isInteger(dateYear / 12)) {
    count = 12;
  }
  rangeStart = dateYear - count + 1;
  return `${rangeStart} - ${rangeStart + 11}`;
};

const FORMATS = {
  DATE: 'yyyy-mm-dd'
};

const CommonDatePicker: React.FC<CommonDatePickerProps> = (props) => {
  const {
    onSelection,
    placeholder = 'Select Date',
    startDate,
    endDate,
    selectsRange = true,
  } = props;

  const [_startDate, setStartDate] = useState<Date | null>(startDate);
  const [_endDate, setEndDate] = useState<Date | null>(endDate);
  const [datePickerType, setDatePickerType] = useState(TEXT_CONSTANTS.DAY);
  const [closeDatePicker, setCloseDatePicker] = useState<boolean>(true);
  const [isDatePickerOpen, setDatePickerOpen] = useState<boolean>(false);

  useEffect(() => {
    setStartDate(startDate);
    setEndDate(endDate);
  }, [startDate, endDate]);

  const onChange = (dates: Array<Date | null> | Date | null) => {
    let start = null;
    let end = null;
    if (Array.isArray(dates)) {
      [start, end] = dates;
    } else {
      start = dates;
    }
    /** show months picker when year is selected,
     * show days picker when month is selected and
     *  set start and end dates when date is selected
     * */
    switch (datePickerType) {
      case TEXT_CONSTANTS.DAY: {
        setStartDate(start);
        setEndDate(end);
        onSelection && onSelection(start, end);
        break;
      }
      case TEXT_CONSTANTS.MONTH: {
        setDatePickerType(TEXT_CONSTANTS.DAY);

        /** Close the datepicker on select under the following conditions
         * Selecting a single date (or) the start and end dates in a range picker
         */
        if (!selectsRange && dates) {
          setCloseDatePicker(true);
        } else if (selectsRange && Array.isArray(dates)) {
          if (dates[0] && dates[1]) {
            setCloseDatePicker(true);
          }
        } else {
          setCloseDatePicker(false);
        }
        break;
      }
      case TEXT_CONSTANTS.YEAR: {
        setDatePickerType(TEXT_CONSTANTS.MONTH);
        break;
      }
      default: {
        console.warn(`Unsupported datepicker change ${datePickerType}`);
      }
    }
  };

  const onDayPickerMonthClick = () => {
    closeDatePicker && setCloseDatePicker(false);
    setDatePickerType(TEXT_CONSTANTS.MONTH);
  };

  const onHeaderYearClick = () => {
    closeDatePicker && setCloseDatePicker(false);
    setDatePickerType(TEXT_CONSTANTS.YEAR);
  };

  const handleCalendarClose = () => {
    isDatePickerOpen && setDatePickerOpen(false);
    setDatePickerType(TEXT_CONSTANTS.DAY);
  };

  return (
    <DatePicker
      inline
      calendarClassName="common-date-picker"
      placeholderText={placeholder}
      selected={_startDate}
      startDate={_startDate}
      endDate={_endDate}
      onChange={onChange}
      {...(selectsRange && { selectsRange: true })}
      shouldCloseOnSelect={closeDatePicker}
      dateFormat={FORMATS.DATE}
      onCalendarClose={handleCalendarClose}
      showYearPicker={TEXT_CONSTANTS.YEAR === datePickerType}
      showMonthYearPicker={TEXT_CONSTANTS.MONTH === datePickerType}
      showFourColumnMonthYearPicker
      renderCustomHeader={({
        date,
        decreaseMonth,
        increaseMonth,
        decreaseYear,
        increaseYear
      }) => (
        <div>
          {datePickerType === TEXT_CONSTANTS.DAY && (
            <CommonDatePickerHeader
              onLeftArrowClick={decreaseMonth}
              onRightArrowClick={increaseMonth}
              date={date}
              onMonthClick={onDayPickerMonthClick}
              onYearClick={onHeaderYearClick}
              dayPicker
            />
          )}
          {datePickerType === TEXT_CONSTANTS.YEAR && (
            <CommonDatePickerHeader
              onLeftArrowClick={decreaseYear}
              onRightArrowClick={increaseYear}
              yearRange={getYearRange(date)}
              yearPicker
            />
          )}
          {datePickerType === TEXT_CONSTANTS.MONTH && (
            <CommonDatePickerHeader
              onLeftArrowClick={decreaseYear}
              onRightArrowClick={increaseYear}
              selectedYear={date.getFullYear().toString()}
              onMonthPickerYearClick={onHeaderYearClick}
              monthPicker
            />
          )}
        </div>
      )}
    />
  );
};

CommonDatePicker.displayName = 'CommonDatePicker';
export { CommonDatePicker };